import React, { useContext, useEffect } from 'react';
import CheckIcon from '@material-ui/icons/Check';
import { Step, StepLabel, Stepper, Typography } from '@material-ui/core';
import { useLocation } from 'react-router-dom';

import AccountCreation from '../AccountCreation/AccountCreation';
import Recording from '../Recording/Recording';
import Tutorial from '../Tutorial/Tutorial';
import './MainSteps.scss';
import { Authorization } from '../../services/authorization';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { setStep } from '../../features/mainStep/mainStepSlice';
import { selectAffiliate } from '../../features/affiliate/affiliateSlice'
import { isPhone } from '../../services/deviceService';
import Payment from '../Payment/Payment';
import SetUserDetails from '../UserDetails/SetUserDetails';
import FinalSteps from '../FinalSteps/FinalSteps';
import CurrentUserContext from '../../contexts/currentUserContext';
import CurrentRecordingContext from '../../contexts/currentRecordingContext';
import { VideoRecordingData } from '../../models/video';
import { Utils } from '../../services/utils';
import { trackMainStep } from '../../services/analyticsService';

export enum MainStepEnum {
  TutorialStep = 0,
  AccountCreationStep,
  RecordingStep,
  DetailsStep,
  PaymentStep,
  FinalStepsStep
}

function getSteps() {
  return ['Tutorial', 'SignUp', 'Snapshot', 'Details', 'Completion', ''];
}

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export default function MainSteps() {
  // user recording additional video
  // New recording means there is no need to show tutorial and proceed with signup. So user is navigate to snapshot step directly
  const [isNewRecording, setIsNewRecording] = React.useState(false);
  const currentUser = useContext(CurrentUserContext);
  const [currentRecording, setCurrentRecording] = React.useState<VideoRecordingData | null>(null)

  console.log(`currentUser: ${JSON.stringify(currentUser)}`);
  
  const [activeStep, setActiveStep] = React.useState(() => {
    const storedStep = localStorage.getItem('activeMainStep');
    return storedStep ? parseInt(storedStep, 10) : 0;
  });
  //TODO: remove the commented code.
  // const [activeStep, setActiveStep] = React.useState(() => 4);

  const dispatch = useAppDispatch();
  const affiliate = useAppSelector(selectAffiliate);
  console.log(`Affiliate selected: ${JSON.stringify(affiliate)}`);
  const steps = getSteps();
  let query = useQuery();

  useEffect(() => {

    const snapshotId = localStorage.getItem("snapshotId");

    if (snapshotId){
      setCurrentRecording({videoId: snapshotId} as VideoRecordingData);
    }

    const beforeunloadCallback = (event: any) => {
      event.preventDefault();
      event.returnValue = "";
      return "";
    };
  
    window.addEventListener("beforeunload", beforeunloadCallback);
      return () => window.removeEventListener("beforeunload", beforeunloadCallback);
  }, []);
  
  useEffect(() => {
    trackMainStep(MainStepEnum[activeStep], activeStep);

    Utils.scrollToTop();

  }, [activeStep]);

  const setUsername = async () => {
    var tokenIsRefreshed = await Authorization.RefreshToken();
    if (!tokenIsRefreshed) {
      console.log("User token can not be refreshed.")
      if (activeStep >= MainStepEnum.RecordingStep) {
        localStorage.removeItem('snapshotId');
        localStorage.removeItem('activeMainStep');
        localStorage.removeItem('activeAccountStep');
        localStorage.removeItem('activeTutorialStep');
        alert("Please log in");
        setTimeout(() => {
          window.location.reload();
        }, 500);
      }
    }
    else {
      const username = await Authorization.GetUsername();

      console.log("redirect already logged in user to Recording page.")
      if (username) {
        // 
      }
      if (query.get("newRecording")) {
        setIsNewRecording(true);
        query.delete("newRecording")
        localStorage.removeItem('snapshotId')
        localStorage.removeItem('activeMainStep')
        dispatch(setStep(MainStepEnum.RecordingStep));
        setActiveStep(MainStepEnum.RecordingStep);
      }
    }
  }

  useEffect(() => {
    setUsername();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (activeStep < MainStepEnum.DetailsStep)
      localStorage.setItem('activeMainStep', activeStep.toString());
  }, [activeStep]);

  function getStepContent(step: number) {
    switch (step) {
      case MainStepEnum.TutorialStep:
        return { component: <Tutorial next={handleNext} /> };
      case MainStepEnum.AccountCreationStep:
        return { component: <AccountCreation next={handleNext} /> };
      case MainStepEnum.RecordingStep:
        return { component: <Recording next={handleNext} /> };
      case MainStepEnum.DetailsStep:
        return { component: <SetUserDetails next={handleNext} /> };
      case MainStepEnum.PaymentStep:
        return { component: <Payment next={handleNext} /> };
      default:
        return { component: <FinalSteps /> };
    }
  }

  const isUserDetailsStepCompleted = () =>
  {
    return currentUser?.User?.dateOfBirth?.trim()
      && currentUser?.User?.address1?.trim()
      && currentUser?.User?.postcode?.trim()
      && currentUser?.User?.town?.trim();
  }

  const isPaymentRequired = async () => {
    const subscriptionIsActive = await Authorization.GetUserSubscriptionStatus();
    const lessThan30DaysAfterPurchase = false;
    return !(subscriptionIsActive && lessThan30DaysAfterPurchase);
  }

  const handleNext = async () => {
    
    // BUG: check if user details are completed and CV is payed before navigation to FinalStepsStep.
    // Currently use is redirected to final steps if isNewRecording is true.
    // For example: user LU expired, 
    // 30 days passed without LU updates, 
    // user never completed payment but started new recording from My account page which sets isNewRecording to true.

    var nextStep = activeStep + 1;

    // Skip Details step use user details are already complete.
    if (nextStep === MainStepEnum.DetailsStep && isUserDetailsStepCompleted())
      nextStep++;

    // Skip payment step if payment is not required.
    if (nextStep === MainStepEnum.PaymentStep && !isPaymentRequired())
      nextStep++;
    
    // navigate user to next step
    setActiveStep((prevActiveStep) => {
      console.log('prevActiveStep', prevActiveStep);
      console.log('nextStep', nextStep);
      dispatch(setStep(prevActiveStep + 1));
      localStorage.setItem('activeAccountStep', "0");
      localStorage.setItem('activeTutorialStep', "0");
      return nextStep;
    });
  };

  const renderFirstStep = () => {
    return (
      <span>
        <span className="desktop">{steps[0]}</span>
        <span className="phone">Tutorials</span>
      </span>
    );
  }

  const renderStepper = () => {
    const renderIcon = (index: number) => {
      if (index !== activeStep) {
        return <CheckIcon className={index < activeStep ? 'completed' : ''} />
      }
      return '';
    }

    if (isPhone()) {
      return (
        <Stepper activeStep={activeStep} className={`main-stepper phone-stepper stepper-step-${activeStep}`}>
          {steps.map((label, index) => {
            if (index === activeStep) {
              return (
                label
                  ? <Step key={label} className={`${index === activeStep ? 'active active-' + index : 'not-active not-active-' + index} ${index === 0 ? 'first-step' : ''}`}>
                    <StepLabel><span>{index === 0 ? renderFirstStep() : label}</span> {renderIcon(index)}</StepLabel>
                  </Step>
                  : <div key={`step-${index}`}></div>
              );
            }
            return (
              label
                ? <Step key={label} className={`${index === activeStep ? 'active active-' + index : 'not-active not-active-' + index} ${index === 0 ? 'first-step' : ''}`}>
                  <StepLabel><span>{index + 1}</span> {renderIcon(index)}</StepLabel>
                </Step>
                : <div key={`step-${index}`}></div>
            );
          })}
        </Stepper>
      );
    }

    return (
      <Stepper activeStep={activeStep} className={`main-stepper stepper-step-${activeStep}`}>
        {steps.map((label, index) => {
          return (
            label
              ? <Step key={label} className={`${index === activeStep ? 'active active-' + index : 'not-active not-active-' + index} ${index === 0 ? 'first-step' : ''}`}>
                <StepLabel><span>{index === 0 ? renderFirstStep() : label}</span> {renderIcon(index)}</StepLabel>
              </Step>
              : <div key={`step-${index}`}></div>
          );
        })}
      </Stepper>
    )
  }

  return (
    <div className="MainSteps">
      {renderStepper()}
      <CurrentRecordingContext.Provider value={{
        VideoRecording: currentRecording,
        SetVideoRecording: setCurrentRecording
      }}>
        <div>
          {
            activeStep === steps.length ? (
              <div>
                <Typography>
                  All steps completed - you&apos;re finished
                </Typography>
              </div>
            ) : getStepContent(activeStep).component
          }
        </div>
      </CurrentRecordingContext.Provider>

    </div >
  );
}